import { Form, FormState } from 'informed';
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AvailableSpace from './ShiftDetail/AvailableSpace';
import Participants, { ParticipantsFormValues } from './ShiftDetail/Participants';
import feathersClient from '../../../apiClient';
import { ErrorAlert, SuccessAlert } from '../../../components/alerts';
import Spinner from '../../../components/Spinner';
import {
  ApplicationData, ApplicationState,
  ManShiftData,
  ShiftData,
} from '../../../types/TymbeApi';
import moment from 'moment';
import CompanyInfo from './ShiftDetail/CompanyInfo';
import ShiftTemplateBox from './ShiftTemplateBox';
import { isDeleted, notDeleted } from './utils';

type ShiftDetailExtendedProps = {
  shiftId: number;
  onShiftRemoved?: (shift: ShiftData) => void;
  toggleShowHistory: () => void;
};


const ShiftDetailExtended = ({ shiftId: id }: ShiftDetailExtendedProps) => {
  const queryClient = useQueryClient();
  const { data: shift, isLoading, isFetching } = useQuery(['shiftDetail', id], async () =>
    feathersClient.service('shift').get(id, {
      query: {
        $eager: '[application(withDeleted).[person.personData(attendanceRate)], manShift.[canceledApplication.[person.personData(attendanceRate)], activeApplication.[person.personData], perk], branchoffice.parent, shiftTemplate, perk, documentType, company]',
        $modify: 'withApplicationCounts',
        $appendJuniorProp: true,
      },
    }));

  const { mutateAsync: removeManShift, isLoading: isRemoving } = useMutation(
    ['remove-man-shift'],
    async (manShiftId: number) =>
      feathersClient.service('man-shift').remove(manShiftId),
    {
      onError: () => {
        ErrorAlert('Nepodařilo se odebrat volné místo ze směny');
      },
      onSuccess: () => {
        SuccessAlert('Volné místo bylo odebráno');
      },
    },
  );

  const { mutateAsync: addOpenManShift, isLoading: isAdding } = useMutation(
    ['create-open-man-shift'],
    async () => feathersClient.service('man-shift').create({ shift_id: shift?.id }),
    {
      onError: () => {
        ErrorAlert('Nepodařilo se přidat místo ke směně');
      },
      onSuccess: () => {
        SuccessAlert('Do objednávky bylo přidáno nové místo');
      },
    },
  );

  const isParticipantChanging = isAdding || isRemoving || isFetching;
  const allowEdit = moment(shift?.start_time).isAfter()
    && !shift?.company?.is_readonly
    && shift?.shift_template_id !== null;

  const createArray = useCallback((length: number) => Array.from({ length }, (_, i) => i), []);

  const onPlus = useCallback(async () => {
    await addOpenManShift();
    queryClient.invalidateQueries();
  }, [addOpenManShift, queryClient]);

  const onMinus = useCallback(async (manShifts: ManShiftData[] | undefined) => {
    const manShiftToRemove = manShifts?.find((ms) => (ms.activeApplication === null || (ms.activeApplication && notDeleted(ms.activeApplication))) && notDeleted(ms));
    if (!manShiftToRemove) {
      ErrorAlert('Z této objednávky již nelze odebrat místa');
      return;
    }
    await removeManShift(manShiftToRemove.id);
    queryClient.invalidateQueries();
  }, [removeManShift, queryClient]);

  const createNewApplication = useCallback(async (
    shiftData: ShiftData,
    tymberId: number,
    manShiftId: number,
  ): Promise<ApplicationData> => {
    const newApplicationData: Partial<ApplicationData> = {
      state: null,
      invitation: true,
      man_shift_id: manShiftId,
      credits: Number(shiftData.invitation_credits),
      payment_base: Number(shiftData.payment_base),
      person_id: tymberId,
      employer_id: shiftData.company_id,
    };
    return feathersClient.service('application').create(newApplicationData);
  }, []);

  const onInvite = useCallback(async (form: FormState<ParticipantsFormValues>, shiftData: ShiftData) => {
    // TODO: error handling
    if (!shiftData.manShift || !form.values.personSelect) return;
    const freeManShifts = shiftData.manShift?.filter((ms) => (ms.activeApplication === null || (ms.activeApplication && notDeleted(ms.activeApplication))) && notDeleted(ms));

    if (freeManShifts && form.values.personSelect.length > freeManShifts.length) {
      const manShiftsToAdd = createArray(form.values.personSelect.length - freeManShifts.length);
      const manshiftPromises = manShiftsToAdd.map(() => addOpenManShift());
      const manShifts = await Promise.all(manshiftPromises);

      freeManShifts.push(...manShifts);
    }

    const promises = form.values.personSelect.map(async (tymber, index) => {
    await createNewApplication(shiftData, tymber.id, freeManShifts[index].id);
    });
    await Promise.all(promises);

    queryClient.invalidateQueries();
  }, [addOpenManShift, createArray, createNewApplication, queryClient]);

  const shiftApplications = shift?.application ? [...shift.application] : [];

  // find latest valid application for tymber
  const latestShiftApplicationsByPersonId = shiftApplications?.reduce((acc: ApplicationData[], currentApplication) => {
    const existingApplication = acc.find((item) => item.person_id === currentApplication.person_id);
    if (!existingApplication) {
      acc.push(currentApplication);
    } else if (moment(currentApplication.created_at).isAfter(moment(existingApplication.created_at))) {
      acc.splice(acc.indexOf(existingApplication), 1);
      acc.push(currentApplication);
    }
    return acc;
  }, []);

  latestShiftApplicationsByPersonId?.sort((prev, next) => {
    const appStateOrder = [
      null, // invitation has no state
      ApplicationState.CONFIRMED,
      ApplicationState.REJECTED,
      ApplicationState.SYSTEM_CANCELED,
      ApplicationState.CANCELED_EARLY,
      ApplicationState.CANCELED_LATE,
    ];

    // order by state first
    if (prev.state !== next.state) {
      return appStateOrder.indexOf(prev.state) - appStateOrder.indexOf(next.state);
    }

    // then by name
    return prev.person?.last_name?.localeCompare(next.person?.last_name ?? '') || 0;
  });

  const excludeParticipants = shift?.application?.filter((app) => isDeleted(app) || app.state === ApplicationState.CONFIRMED).map((app) => app.person_id);

  return (
    <div className="flex flex-col gap-4 overflow-auto max-h-full">
      <Spinner show={isParticipantChanging} className="h-8" />
      {!isParticipantChanging && shift
        && (
          <Form className=''>
              <div className="flex flex-col gap-2 mb-4">
                <ShiftTemplateBox shift={shift} />
                <CompanyInfo shift={shift} />
              </div>
              <AvailableSpace
                className="text-secondary-600 mb-4"
                valueTotal={(
                  <>
                    <Spinner show={isParticipantChanging} className="size-6 self-center justify-self-center" absolute />
                    {Number(shift.orders_count)}
                  </>
                )}
                valueUnfilled={(
                  <>
                    <Spinner show={isParticipantChanging} className="size-6 self-center justify-self-center" absolute />
                    {Number(shift.unfilled_orders_count)}
                  </>
                )}
                disabled={isParticipantChanging || !allowEdit}
                onPlus={onPlus}
                onMinus={() => onMinus(shift.manShift)}
              />
              <Participants
                current={Number(shift.filled_orders_count) || 0}
                total={shift.manShift?.length || 0}
                applications={latestShiftApplicationsByPersonId}
                exclude={excludeParticipants}
                className="text-secondary-900"
                onInvite={(formState) => onInvite(formState, shift)}
                shift={shift}
                disabled={!allowEdit}
              />
            </Form>
        )}
    </div>
  );
};

export default ShiftDetailExtended;

